import { useCallback, useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import type { AutocompleteOption } from "@/components/LockedAutocomplete";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { ApiError } from "@/lib/apiClient";
import {
  calendarDayTitle,
  calendarDaysToMap,
  getCalendarDays,
  type EmployeeCalendarDay,
} from "@/lib/employeeCalendar";
import {
  expandEffectiveAbsenceDays,
  listAbsenceRequests,
  type AbsenceDayMarker,
  type EmployeeAbsenceRequest,
} from "@/lib/employeeAbsences";
import {
  formatHours,
  getEmployeeYearSummary,
  type EmployeeTimeYearSummary,
} from "@/lib/employeeTimes";
import {
  expandHeroAbsenceDays,
  getHeroEmployeeCalendar,
  heroHoursByDayMap,
} from "@/lib/heroEmployeeHr";
import {
  AbsenceCalendarLegend,
  AbsenceDayBadges,
  HeroAbsenceDayBadges,
} from "@/pages/admin/zeiterfassung/AbsenceCalendarLegend";
import { CalendarDayBadges } from "@/pages/admin/zeiterfassung/CalendarDayBadge";
import {
  WORK_TIME_DAY_COLUMNS,
  getWorkTimeDayColumnDefaultWidth,
  getWorkTimeDayColumnLabel,
  type WorkTimeDayColumnKey,
} from "@/pages/admin/zeiterfassung/columns";
import {
  EmployeeLockedAutocomplete,
  employeeLoginToOption,
  type EmployeeOption,
} from "@/pages/admin/zeiterfassung/EmployeeLockedAutocomplete";
import { TimesExportButton } from "@/pages/admin/zeiterfassung/TimesExportButton";
import { useRefreshKeyLoad } from "@/pages/admin/zeiterfassung/useRefreshKeyLoad";
import { useZeiterfassungColumnWidths } from "@/pages/admin/zeiterfassung/useZeiterfassungColumnWidths";
import {
  ZeiterfassungDataTable,
  type ZeiterfassungTableColumn,
} from "@/pages/admin/zeiterfassung/ZeiterfassungDataTable";
import {
  formatOvertimeBalanceHhmm,
  isWeekend,
  MONTH_LONG,
  todayIso,
} from "@/pages/admin/zeiterfassung/yearOverviewUtils";

interface WorkTimeDayRow {
  iso: string;
  month: number;
  soll: number;
  ist: number;
  saldo: number;
  stand: number | null;
  isWeekend: boolean;
  calendarDay: EmployeeCalendarDay | null;
  absenceMarkers: AbsenceDayMarker[];
  heroAbsenceMarkers: AbsenceDayMarker[];
}

interface WorkTimeTablePanelProps {
  refreshKey: number;
  defaultEmployeeLogin?: string;
  defaultEmployeeDisplay?: string;
  allowEmployeePicker?: boolean;
  includeHeroOverlay?: boolean;
}

export function WorkTimeTablePanel({
  refreshKey,
  defaultEmployeeLogin,
  defaultEmployeeDisplay,
  allowEmployeePicker = true,
  includeHeroOverlay = false,
}: WorkTimeTablePanelProps) {
  const now = new Date();
  const [selectedEmployee, setSelectedEmployee] = useState<AutocompleteOption<EmployeeOption> | null>(
    () => employeeLoginToOption(defaultEmployeeLogin || "", defaultEmployeeDisplay),
  );
  const employeeLogin = selectedEmployee?.value.login ?? "";
  const employeeDisplay = selectedEmployee?.value.display ?? "";
  const [year, setYear] = useState(now.getFullYear());
  const [monthFilter, setMonthFilter] = useState<number>(0); // 0 = ganzes Jahr
  const [summary, setSummary] = useState<EmployeeTimeYearSummary | null>(null);
  const [absences, setAbsences] = useState<EmployeeAbsenceRequest[]>([]);
  const [calendarByDay, setCalendarByDay] = useState<Map<string, EmployeeCalendarDay>>(new Map());
  const [heroHoursByDay, setHeroHoursByDay] = useState<Map<string, number>>(new Map());
  const [heroAbsenceByDay, setHeroAbsenceByDay] = useState<Map<string, AbsenceDayMarker[]>>(new Map());
  const [heroLinked, setHeroLinked] = useState(false);
  const [heroPartnerName, setHeroPartnerName] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { widths, onColumnWidthChange } = useZeiterfassungColumnWidths("work-time-year-days");

  useEffect(() => {
    if (!allowEmployeePicker && defaultEmployeeLogin) {
      setSelectedEmployee(
        employeeLoginToOption(defaultEmployeeLogin, defaultEmployeeDisplay),
      );
    }
  }, [allowEmployeePicker, defaultEmployeeLogin, defaultEmployeeDisplay]);

  const load = useCallback(async (options?: { silent?: boolean }) => {
    if (!employeeLogin) {
      setSummary(null);
      setAbsences([]);
      setCalendarByDay(new Map());
      setHeroHoursByDay(new Map());
      setHeroAbsenceByDay(new Map());
      setHeroLinked(false);
      setHeroPartnerName(null);
      return;
    }
    if (!options?.silent) setLoading(true);
    setError(null);
    try {
      const heroPromise = includeHeroOverlay
        ? getHeroEmployeeCalendar({ year, user_login: employeeLogin })
        : Promise.resolve(null);
      const [data, absenceRows, calendar, heroData] = await Promise.all([
        getEmployeeYearSummary({ user_login: employeeLogin, year }),
        listAbsenceRequests({ user_login: employeeLogin, year }),
        getCalendarDays({ user_login: employeeLogin, year }),
        heroPromise,
      ]);
      setSummary(data);
      setAbsences(absenceRows);
      setCalendarByDay(calendarDaysToMap(calendar.days));
      if (heroData) {
        setHeroHoursByDay(heroHoursByDayMap(heroData.hours_by_day));
        setHeroAbsenceByDay(expandHeroAbsenceDays(heroData.absences));
        setHeroLinked(heroData.linked);
        setHeroPartnerName(heroData.partner_name);
      } else {
        setHeroHoursByDay(new Map());
        setHeroAbsenceByDay(new Map());
        setHeroLinked(false);
        setHeroPartnerName(null);
      }
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : "Arbeitszeiten konnten nicht geladen werden.",
      );
      setSummary(null);
      setAbsences([]);
      setCalendarByDay(new Map());
      setHeroHoursByDay(new Map());
      setHeroAbsenceByDay(new Map());
      setHeroLinked(false);
      setHeroPartnerName(null);
    } finally {
      setLoading(false);
    }
  }, [employeeLogin, year, includeHeroOverlay]);

  useRefreshKeyLoad(load, refreshKey);

  const absenceByDay = useMemo(
    () => expandEffectiveAbsenceDays(absences, calendarByDay, { year, includePending: true }),
    [absences, calendarByDay, year],
  );

  const appHoursByDay = useMemo(() => {
    const map = new Map<string, number>();
    for (const month of summary?.months ?? []) {
      for (const day of month.days) {
        const iso = `${year}-${String(month.month).padStart(2, "0")}-${String(day.day).padStart(2, "0")}`;
        map.set(iso, Number(day.total_hours || 0));
      }
    }
    return map;
  }, [summary, year]);

  const allRows = useMemo((): WorkTimeDayRow[] => {
    const sollByDay = summary?.expected_hours_by_day ?? {};
    const standByDay = summary?.overtime_balance_by_day ?? null;
    const rows: WorkTimeDayRow[] = [];
    for (let month = 1; month <= 12; month++) {
      const daysInMonth = new Date(year, month, 0).getDate();
      for (let day = 1; day <= daysInMonth; day++) {
        const iso = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
        const appHours = appHoursByDay.get(iso) ?? 0;
        const heroHours = includeHeroOverlay ? (heroHoursByDay.get(iso) ?? 0) : 0;
        const ist = appHours + heroHours;
        const soll = Number(sollByDay[iso] ?? 0);
        const standRaw = standByDay && iso in standByDay ? Number(standByDay[iso]) : null;
        rows.push({
          iso,
          month,
          soll,
          ist,
          saldo: ist - soll,
          stand: standRaw != null && Number.isFinite(standRaw) ? standRaw : null,
          isWeekend: isWeekend(year, month, day),
          calendarDay: calendarByDay.get(iso) ?? null,
          absenceMarkers: absenceByDay.get(iso) ?? [],
          heroAbsenceMarkers: includeHeroOverlay
            ? (heroAbsenceByDay.get(iso) ?? [])
            : [],
        });
      }
    }
    return rows;
  }, [
    summary,
    year,
    appHoursByDay,
    includeHeroOverlay,
    heroHoursByDay,
    calendarByDay,
    absenceByDay,
    heroAbsenceByDay,
  ]);

  const visibleRows = useMemo(
    () => (monthFilter > 0 ? allRows.filter((r) => r.month === monthFilter) : allRows),
    [allRows, monthFilter],
  );

  const hasSoll = summary?.has_soll !== false;

  const columns = useMemo(
    (): ZeiterfassungTableColumn<WorkTimeDayRow>[] =>
      WORK_TIME_DAY_COLUMNS.filter((key) =>
        hasSoll ? true : key === "date" || key === "hinweis" || key === "ist",
      ).map((key: WorkTimeDayColumnKey) => ({
        key,
        label: getWorkTimeDayColumnLabel(key),
        defaultWidth: getWorkTimeDayColumnDefaultWidth(key),
        mono: key !== "date" && key !== "hinweis",
        align: key === "date" || key === "hinweis" ? "left" : "right",
        sortValue: (row) => {
          if (key === "date") return row.iso;
          if (key === "hinweis") {
            return [
              row.calendarDay?.kind ?? "",
              row.calendarDay?.label ?? "",
              row.absenceMarkers.map((m) => m.absence_type).join(","),
              row.heroAbsenceMarkers.map((m) => m.absence_type).join(","),
            ].join("|");
          }
          if (key === "soll") return row.soll;
          if (key === "ist") return row.ist;
          if (key === "saldo") return row.saldo;
          if (key === "stand") return row.stand ?? Number.NEGATIVE_INFINITY;
          return "";
        },
        render: (row) => {
          switch (key) {
            case "date": {
              const isToday = row.iso === todayIso();
              return (
                <span
                  className={clsx(
                    "inline-flex flex-wrap items-center gap-1.5 font-medium",
                    isToday ? "text-accent" : "text-label",
                  )}
                >
                  {new Date(`${row.iso}T12:00:00`).toLocaleDateString("de-DE", {
                    weekday: "short",
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                  })}
                  {isToday ? (
                    <span className="inline-flex rounded-md bg-accent-soft px-1.5 py-0.5 text-[10px] font-bold text-accent ring-1 ring-accent/40">
                      Heute
                    </span>
                  ) : null}
                </span>
              );
            }
            case "hinweis":
              return (
                <div className="flex min-w-0 flex-wrap items-center gap-1.5">
                  {row.isWeekend && (
                    <span className="inline-flex rounded-md bg-fill-secondary px-1.5 py-0.5 text-[10px] font-bold text-label-secondary ring-1 ring-separator">
                      WE
                    </span>
                  )}
                  {row.calendarDay && (
                    <span
                      className="inline-flex min-w-0 max-w-full items-center gap-1"
                      title={calendarDayTitle(row.calendarDay)}
                    >
                      <CalendarDayBadges days={[row.calendarDay]} />
                      {row.calendarDay.kind === "holiday" && row.calendarDay.label ? (
                        <span className="truncate text-xs font-medium text-amber-900 dark:text-amber-200">
                          {row.calendarDay.label}
                        </span>
                      ) : null}
                    </span>
                  )}
                  <AbsenceDayBadges markers={row.absenceMarkers} />
                  <HeroAbsenceDayBadges markers={row.heroAbsenceMarkers} />
                  {!row.isWeekend &&
                    !row.calendarDay &&
                    row.absenceMarkers.length === 0 &&
                    row.heroAbsenceMarkers.length === 0 && (
                      <span className="text-label-tertiary">—</span>
                    )}
                </div>
              );
            case "soll":
              return formatHours(row.soll);
            case "ist":
              return row.ist > 0 ? formatHours(row.ist) : "—";
            case "saldo": {
              if (Math.abs(row.saldo) < 0.005 && row.soll <= 0 && row.ist <= 0) {
                return <span className="text-label-tertiary">—</span>;
              }
              return (
                <span
                  className={clsx(
                    "tabular-nums font-medium",
                    row.saldo > 0.005
                      ? "text-green-600 dark:text-green-400"
                      : row.saldo < -0.005
                        ? "text-red-600 dark:text-red-400"
                        : "text-label",
                  )}
                >
                  {formatHours(row.saldo)}
                </span>
              );
            }
            case "stand":
              return row.stand != null ? (
                <span className="tabular-nums font-medium text-label">
                  {formatOvertimeBalanceHhmm(row.stand)}
                </span>
              ) : (
                <span className="text-label-tertiary">—</span>
              );
            default:
              return null;
          }
        },
      })),
    [hasSoll],
  );

  const rowClassName = (row: WorkTimeDayRow): string | undefined => {
    let base: string | undefined;
    // Spezielle Tage überlagern immer die Ausgrauung (Soll = 0).
    if (row.calendarDay?.kind === "holiday") {
      base = "bg-amber-500/15 dark:bg-amber-500/10";
    } else if (
      (row.calendarDay?.kind === "bridge" || row.calendarDay?.kind === "betriebsferien") &&
      !row.calendarDay.works_instead
    ) {
      base = "bg-indigo-500/12 dark:bg-indigo-500/10";
    } else if (row.absenceMarkers.length > 0 || row.heroAbsenceMarkers.length > 0) {
      base = "bg-sky-500/10 dark:bg-sky-500/10";
    } else if (row.isWeekend || (hasSoll && row.soll <= 0.005)) {
      // Wochenende / Modell-Nichtarbeitstag (Soll 0): komplett ausgegraut.
      base = "bg-fill-secondary/80 text-label-tertiary [&_td]:text-label-tertiary";
    }

    const isToday = row.iso === todayIso();
    if (!base && !isToday) return undefined;
    return clsx(
      base,
      // Heute: Accent-Rahmen (überlagert Spezialfarben nicht).
      isToday && "relative z-[1] shadow-[inset_0_0_0_2px_var(--color-accent)] ring-2 ring-accent/35",
      isToday && !base && "bg-accent-soft/50",
    );
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 rounded-ios border border-separator bg-card p-4 shadow-ios md:grid-cols-4">
        {allowEmployeePicker ? (
          <EmployeeLockedAutocomplete
            value={selectedEmployee}
            onChange={setSelectedEmployee}
          />
        ) : (
          <label className="space-y-1">
            <span className="eh-label">Mitarbeiter</span>
            <div className="eh-input flex items-center bg-fill-tertiary/40 text-label">
              {employeeDisplay || employeeLogin}
            </div>
          </label>
        )}
        <label className="space-y-1">
          <span className="eh-label">Jahr</span>
          <select
            className="eh-input w-full"
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
          >
            {[now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1].map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </label>
        <label className="space-y-1">
          <span className="eh-label">Monat</span>
          <select
            className="eh-input w-full"
            value={monthFilter}
            onChange={(e) => setMonthFilter(Number(e.target.value))}
          >
            <option value={0}>Ganzes Jahr</option>
            {MONTH_LONG.map((name, idx) => (
              <option key={name} value={idx + 1}>
                {name}
              </option>
            ))}
          </select>
        </label>
        <div className="flex flex-wrap items-end gap-2">
          <Button variant="secondary" onClick={() => void load()} disabled={!employeeLogin}>
            Laden
          </Button>
          <TimesExportButton
            employeeLogin={employeeLogin}
            year={year}
            includeHero={includeHeroOverlay}
            disabled={!employeeLogin}
          />
        </div>
      </div>

      {!employeeLogin && allowEmployeePicker && (
        <div className="rounded-ios border border-dashed border-separator bg-card p-8 text-center text-sm text-label-secondary">
          Bitte einen Mitarbeiter auswählen, um die Arbeitszeiten anzuzeigen.
        </div>
      )}

      {error && <Alert variant="error" title="Fehler">{error}</Alert>}

      {employeeLogin && loading && (
        <div className="flex justify-center py-10">
          <Spinner />
        </div>
      )}

      {employeeLogin && !loading && summary && (
        <>
          <div>
            <div className="mb-1 flex flex-wrap items-baseline justify-between gap-2">
              <h3 className="text-sm font-semibold text-label">
                Arbeitszeiten {year}
                {monthFilter > 0 ? ` · ${MONTH_LONG[monthFilter - 1]}` : ""}
              </h3>
              {includeHeroOverlay && heroLinked && heroPartnerName ? (
                <span className="text-xs text-label-secondary">Hero: {heroPartnerName}</span>
              ) : includeHeroOverlay && !heroLinked ? (
                <span className="text-xs text-label-secondary">Hero nicht verknüpft</span>
              ) : null}
            </div>
            <p className="mb-3 text-xs text-label-secondary">
              {hasSoll
                ? "Soll laut Arbeitszeitmodell (abzgl. FT/Abw.). Saldo = Ist − Soll. Aktueller Stand = laufender Überstundenstand ab App-Startstand."
                : "Erfasste Ist-Stunden je Tag (ohne Soll und ohne Überstundenkonto)."}
            </p>
            {hasSoll && summary.overtime_balance_by_day == null && (
              <Alert variant="info" title="Aktueller Stand nicht verfügbar" className="mb-3">
                Für den laufenden Stand wird ein App-Startstand benötigt (Mitarbeiter →
                Überstunden-Startstand).
              </Alert>
            )}
            <div className="mb-3">
              <AbsenceCalendarLegend showHeroLegend={includeHeroOverlay && heroLinked} />
            </div>
            <ZeiterfassungDataTable
              rows={visibleRows}
              columns={columns}
              rowKey={(row) => row.iso}
              widths={widths}
              onColumnWidthChange={onColumnWidthChange}
              emptyText="Keine Tage im gewählten Zeitraum."
              rowClassName={rowClassName}
              renderMobileCard={(row) => (
                <div
                  className={clsx(
                    "rounded-ios border border-separator px-4 py-3 shadow-ios",
                    rowClassName(row) ?? "bg-card",
                  )}
                >
                  <div
                    className={clsx(
                      "inline-flex flex-wrap items-center gap-1.5 font-medium",
                      row.iso === todayIso() ? "text-accent" : "text-label",
                    )}
                  >
                    {new Date(`${row.iso}T12:00:00`).toLocaleDateString("de-DE", {
                      weekday: "long",
                      day: "2-digit",
                      month: "2-digit",
                      year: "numeric",
                    })}
                    {row.iso === todayIso() ? (
                      <span className="inline-flex rounded-md bg-accent-soft px-1.5 py-0.5 text-[10px] font-bold text-accent ring-1 ring-accent/40">
                        Heute
                      </span>
                    ) : null}
                  </div>
                  <div className="mt-1.5 flex min-w-0 flex-wrap items-center gap-1.5">
                    {row.isWeekend && (
                      <span className="inline-flex rounded-md bg-fill-secondary px-1.5 py-0.5 text-[10px] font-bold text-label-secondary ring-1 ring-separator">
                        WE
                      </span>
                    )}
                    {row.calendarDay && (
                      <span className="inline-flex min-w-0 items-center gap-1">
                        <CalendarDayBadges days={[row.calendarDay]} />
                        {row.calendarDay.kind === "holiday" && row.calendarDay.label ? (
                          <span className="truncate text-xs font-medium text-amber-900 dark:text-amber-200">
                            {row.calendarDay.label}
                          </span>
                        ) : null}
                      </span>
                    )}
                    <AbsenceDayBadges markers={row.absenceMarkers} />
                    <HeroAbsenceDayBadges markers={row.heroAbsenceMarkers} />
                  </div>
                  <div className="mt-2 grid grid-cols-2 gap-2 text-sm">
                    {hasSoll && (
                      <div>
                        <div className="text-xs text-label-secondary">Soll</div>
                        <div className="tabular-nums text-label">{formatHours(row.soll)}</div>
                      </div>
                    )}
                    <div>
                      <div className="text-xs text-label-secondary">Ist</div>
                      <div className="tabular-nums text-label">
                        {row.ist > 0 ? formatHours(row.ist) : "—"}
                      </div>
                    </div>
                    {hasSoll && (
                      <>
                        <div>
                          <div className="text-xs text-label-secondary">Saldo</div>
                          <div
                            className={clsx(
                              "tabular-nums font-medium",
                              row.saldo > 0.005
                                ? "text-green-600 dark:text-green-400"
                                : row.saldo < -0.005
                                  ? "text-red-600 dark:text-red-400"
                                  : "text-label",
                            )}
                          >
                            {formatHours(row.saldo)}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs text-label-secondary">Aktueller Stand</div>
                          <div className="tabular-nums font-medium text-label">
                            {row.stand != null ? formatOvertimeBalanceHhmm(row.stand) : "—"}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}
            />
          </div>
        </>
      )}
    </div>
  );
}
